// ONE-CLICK [temporary — see docs/refactoring/ONE-CLICK-UPGRADE-FEATURE.md]
//
// Orchestrates the One Click upgrade for Solarman inverters:
//  - support-types detection (authority for ready vs armFirst)
//  - ARM-first step (solarman.iot) and full One Click (solarman.api)
// Progress comes from the onPlantStatusUpdate subscription via the store
// (selectOneClickPushState) — getLoggerData returns inverter[] null, so
// there is no polling fallback.
// Reuses the existing Solarman HTTP plumbing + CRC util so it drops cleanly.

import { Injectable } from '@angular/core';
import gql from 'graphql-tag';

import { CallPROSolarmanLastActionResponse, InverterUpgradeConfig } from 'ngx-modbus';
import {
  InverterUpgradeInput,
  SendInverterUpgradeMutation,
  SolarmanPackageInput,
} from 'ngx-core-services';

import { SolarmanService } from '../../../communication/solarman.service';
import { SolarmanRateLimitError } from '../../../communication/upgrade.service';
import { createDebugLogger, DebugConfig } from '../../shared/debug';
import { computeCRC32 } from '../upgrade-inverter.utils';
import { oneClickCrc32 } from './one-click-crc';
import { SgpInteraction } from '../../../SGP/sgp-interaction.model';
import {
  OneClickChannel,
  OneClickGate,
  OneClickModulePlan,
  SolarmanSupportTypes,
  evaluateOneClickGate,
} from './one-click.utils';

const DEBUG_CONFIG: DebugConfig = { ENABLED: false, LOG_API: true, LOG_STATE: true, LOG_DATA: true };
const Logger = createDebugLogger('[OneClickUpgrade]', DEBUG_CONFIG);

/**
 * Order timeout for the full one-click (4 modules + reboots). Value confirmed
 * from a working manual mutation on the Test endpoint (doc §3: timeoutSeconds 1200).
 */
export const ONE_CLICK_TIMEOUT_SECONDS = 1200;

const SUPPORT_TYPES_URL = '/order-s/order/action/firmware/device/support-types';

/**
 * Gate evaluation runs right after the page-entry burst of Solarman PRO calls
 * (communication info + fw lists in parallel) — the first support-types call
 * deterministically hits the rate limiter there. Retry with backoff before
 * resolving the gate to `unknown`.
 */
const SUPPORT_TYPES_MAX_ATTEMPTS = 3;
const SUPPORT_TYPES_RETRY_BASE_DELAY_MS = 2000;

const responseStatusFragment = gql`
  fragment responsestatustype on ResponseStatusType {
    success
    statusCode
    message
  }
`;

@Injectable({ providedIn: 'root' })
export class OneClickUpgradeService {
  constructor(
    private readonly solarmanService: SolarmanService,
    private readonly sendInverterUpgradeMutation: SendInverterUpgradeMutation,
  ) {}

  /**
   * Resolve the one-click gate. Kill-switch and Ethernet are decided locally
   * (no network call); otherwise the Solarman support-types API is queried.
   * Any failure → supportTypes null → gate `unknown` (never legacy fallback).
   */
  async evaluateGate(
    config: InverterUpgradeConfig | null | undefined,
    channel: OneClickChannel,
    solarmanDeviceId: string | number | null | undefined,
  ): Promise<OneClickGate> {
    if (!config?.MIN_ONE_CLICK_ARM || channel === 'Ethernet') {
      return evaluateOneClickGate(config, channel, null);
    }

    let supportTypes: SolarmanSupportTypes | null = null;
    if (solarmanDeviceId != null) {
      supportTypes = await this.getSupportTypesWithRetry(solarmanDeviceId);
    }
    return evaluateOneClickGate(config, channel, supportTypes);
  }

  /** support-types with backoff (2s, 4s); null after the last failed attempt → gate `unknown`. */
  private async getSupportTypesWithRetry(solarmanDeviceId: string | number): Promise<SolarmanSupportTypes | null> {
    for (let attempt = 1; attempt <= SUPPORT_TYPES_MAX_ATTEMPTS; attempt++) {
      try {
        return await this.getSupportTypes(solarmanDeviceId);
      } catch (error) {
        Logger.warn(`getSupportTypes attempt ${attempt}/${SUPPORT_TYPES_MAX_ATTEMPTS} failed ` + JSON.stringify({ error: `${error}` }));
        if (attempt < SUPPORT_TYPES_MAX_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, SUPPORT_TYPES_RETRY_BASE_DELAY_MS * attempt));
        }
      }
    }
    return null;
  }

  /** GET support-types for the inverter's Solarman deviceId. */
  async getSupportTypes(solarmanDeviceId: string | number): Promise<SolarmanSupportTypes> {
    await this.solarmanService.authenticate();
    const raw = await this.solarmanService.callApiSolarman(
      SUPPORT_TYPES_URL,
      {},
      'callPROSolarman',
      'GET',
      { deviceId: solarmanDeviceId },
    );
    const parsed = JSON.parse(raw) as SolarmanSupportTypes & { code?: string };
    Logger.api('support-types ' + JSON.stringify({ deviceId: solarmanDeviceId, parsed }));
    if (parsed?.code === 'OVER_MAXIMUM_LIMITS') {
      throw new SolarmanRateLimitError(SUPPORT_TYPES_URL);
    }
    return { types: parsed?.types ?? [] };
  }

  /**
   * Pull fallback for the one-click progress: the api path creates a Solarman
   * ORDER, so lastAction responds for the inverter (before the first order the
   * endpoint throws `sorry_for_server_exception`). Error bodies and thrown
   * calls degrade to null — the caller keeps waiting on the push channel.
   */
  async getLastAction(solarmanDeviceId: string | number): Promise<CallPROSolarmanLastActionResponse | null> {
    try {
      await this.solarmanService.authenticate();
      const lastAction = await this.solarmanService.deviceLastActionRequest(`${solarmanDeviceId}`);
      if ((lastAction as any)?.code != null) {
        Logger.api('getLastAction unavailable ' + JSON.stringify({ code: (lastAction as any).code }));
        return null;
      }
      return lastAction;
    } catch (error) {
      Logger.warn('getLastAction failed ' + JSON.stringify({ error: `${error}` }));
      return null;
    }
  }

  /**
   * Send the upgrade mutation and verify the backend outcome. A resolved
   * GraphQL call only means the request was transported: a `success: false`
   * body means NO order was created device-side, so it must throw — the
   * callers arm optimistic flags and progress phases on resolve.
   */
  private async executeUpgrade(input: InverterUpgradeInput, label: string): Promise<void> {
    const result = await this.sendInverterUpgradeMutation.execute(responseStatusFragment, { input });
    const status = result.data?.sendInverterUpgrade;
    if (status?.success !== true) {
      Logger.error(`${label} rejected by backend ` + JSON.stringify({ statusCode: status?.statusCode, message: status?.message }));
      throw new Error(`${label} rejected by backend (statusCode=${status?.statusCode ?? 'n/a'}): ${status?.message ?? 'no message'}`);
    }
  }

  /** ARM-first step via the legacy single-file channel (solarman.iot, fileType 0 = ARM). */
  async sendArmFirst(p: { loggerId: string; inverterId: string; armLink: string }): Promise<void> {
    // Legacy iot path → standard reflected CRC32 (same as the per-module flow).
    const buffer = await this.downloadBin(p.armLink);
    const size = buffer.byteLength;
    const crc = computeCRC32(buffer);
    const input: InverterUpgradeInput = {
      solarman: {
        iot: {
          loggerId: p.loggerId,
          inverterId: p.inverterId,
          fileType: 0,
          fileCRC: `${crc}`,
          fileURL: p.armLink.replace('https', 'http'),
          fileSize: `${size}`,
        },
      },
    };
    Logger.api('sendArmFirst ' + JSON.stringify({ loggerId: p.loggerId, inverterId: p.inverterId, size, crc }));
    await this.executeUpgrade(input, 'sendArmFirst');
  }

  /** Full One Click via the multi-package channel (solarman.api). */
  async sendOneClick(p: { loggerId: string; deviceSN: string; plan: OneClickModulePlan[] }): Promise<void> {
    const packages = await Promise.all(p.plan.map((item) => this.preparePackage(item)));
    const input: InverterUpgradeInput = {
      solarman: {
        api: {
          loggerId: p.loggerId,
          deviceSN: p.deviceSN,
          timeoutSeconds: ONE_CLICK_TIMEOUT_SECONDS,
          packages,
        },
      },
    };
    Logger.api('sendOneClick ' + JSON.stringify({ loggerId: p.loggerId, deviceSN: p.deviceSN, modules: p.plan.map((x) => x.module) }));
    await this.executeUpgrade(input, 'sendOneClick');
  }

  /** Download the .bin over its (https) link. */
  private async downloadBin(link: string): Promise<ArrayBuffer> {
    const response = await fetch(link);
    // fetch resolves on HTTP errors: an S3 403/404 XML body would otherwise
    // pass the emptiness check and get CRC'd as firmware.
    if (!response.ok) {
      throw new Error(`Error downloading firmware update (HTTP ${response.status}): ${link}`);
    }
    const buffer = await response.arrayBuffer();
    if (!buffer) {
      throw new Error('Error downloading firmware update');
    }
    return buffer;
  }

  /**
   * Map a module plan entry to a SolarmanPackageInput for the api flow.
   * Contract confirmed from a working manual mutation (doc §3): ipUrl === domainUrl,
   * both http; crc decimal string; size number; expectedVersion omitted.
   * ONE-CLICK CRC: the inverter variant (oneClickCrc32), NOT the standard reflected
   * CRC32 — verified to reproduce the reference package values.
   * The .bin is fetched over its original (https) link — the http URLs are only
   * what the backend uses to pull the files.
   */
  private async preparePackage(item: OneClickModulePlan): Promise<SolarmanPackageInput> {
    const buffer = await this.downloadBin(item.link);
    const bytes = new Uint8Array(buffer);
    const size = buffer.byteLength;
    const crc = oneClickCrc32(bytes);
    // Target version from the firmware metadata footer (official SGP routine).
    // undefined → omitted (e.g. SAFETY, whose version field is empty).
    const expectedVersion = this.extractTargetVersion(bytes);
    const httpUrl = item.link.replace('https', 'http');
    Logger.api('preparePackage ' + JSON.stringify({ module: item.module, size, crc, expectedVersion }));
    return {
      ipUrl: httpUrl,
      domainUrl: httpUrl,
      name: item.link.split('/').pop() ?? item.module,
      size,
      crc: `${crc}`,
      expectedVersion,
    };
  }

  /**
   * Target firmware version from the .bin metadata footer, via the official SGP
   * routine (handles V1/V2/V3 structures). Returns undefined when absent/empty
   * (e.g. SAFETY, timestamp-named) or on parse error → expectedVersion omitted.
   */
  private extractTargetVersion(bytes: Uint8Array): string | undefined {
    try {
      const version = SgpInteraction.ExtractMetadataFromFirmware(bytes)?.firmwareVersion?.trim();
      return version ? version : undefined;
    } catch (error) {
      Logger.warn('extractTargetVersion failed ' + JSON.stringify({ error: `${error}` }));
      return undefined;
    }
  }
}
